import React from 'react'
import { Label } from '@/components/ui/label'

interface AuthFormFieldOwnProps {
  id: string
  label: string
  error?: string
}

type AuthFormFieldProps = AuthFormFieldOwnProps &
  Omit<React.InputHTMLAttributes<HTMLInputElement>, keyof AuthFormFieldOwnProps>

/**
 * AuthFormField — reusable floating-label + input + error trio for auth forms.
 *
 * Supports both controlled (value/onChange) and uncontrolled
 * (React Hook Form `register` spread via ref) usage.
 *
 * Accessibility:
 * - `<label htmlFor>` is linked to `<input id>` via the `id` prop.
 * - Error message is linked via `aria-describedby` so screen readers announce it.
 * - `aria-invalid` flags the field to assistive tech when an error is present.
 * Uses `.input-site` / `.float-field` / `.float-label` CSS classes for visual consistency.
 */
export const AuthFormField = React.forwardRef<HTMLInputElement, AuthFormFieldProps>(
  function AuthFormField(
    // placeholder is accepted for backwards compatibility but intentionally
    // unused — the floating label now occupies the placeholder position.
    { id, label, error, required, placeholder: _placeholder, ...rest },
    ref,
  ) {
    const errorId = `${id}-error`

    return (
      <div className="flex flex-col">
        <div className="float-field">
          <input
            ref={ref}
            id={id}
            required={required}
            aria-invalid={!!error}
            aria-describedby={error ? errorId : undefined}
            placeholder=" "
            className="input-site disabled:opacity-50 disabled:cursor-not-allowed"
            {...rest}
          />
          <Label htmlFor={id} className="float-label">
            {label}
            {required && (
              <span className="text-destructive ml-1" aria-hidden="true">
                *
              </span>
            )}
          </Label>
        </div>
        {error && (
          <p id={errorId} role="alert" className="mt-2 text-red-500 text-sm">
            {error}
          </p>
        )}
      </div>
    )
  },
)
